import React, { FC, memo, useMemo, useState } from 'react';
import { Segment, Row, Col, StatusTip, Radio, Select } from 'tea-component';
import { RevenueUnitsSegment } from '../../_config/constant';
import CardSlotV2 from '@/pages/intelligence/components/CardSlotV2';

import TipContext from '@/pages/home/components/TipContext';
import TrendsBarChart from './TrendsBarChart';
import RegionalPieChart from './RegionalPieChart';
import { useIntl, getLocale } from 'umi';
import Summary from '../Summary';
import { TimePeriodType } from '../../types';

import './index.scss';

const { LoadingTip } = StatusTip;

interface RevenueTrendsProps {
  loading: boolean;
  trendsData: any;
  segment: string;
  summaryData: any[];
  revenueRegional: any;
  levelValue: any;
  timeBasis: string;
  timePeriod: TimePeriodType;
  onTimeBasicChange: (value: string) => void;
  onTimePeriodChange: (value: TimePeriodType) => void;
}

const RevenueTrends: FC<RevenueTrendsProps> = (props) => {
  const {
    loading,
    trendsData,
    segment,
    summaryData = [],
    revenueRegional = {},
    timeBasis,
    levelValue,
    onTimeBasicChange,
    timePeriod,
    onTimePeriodChange,
  } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [activeSegment, setActiveSegment] = useState('revenue');

  const segmentList = useMemo(() => {
    return RevenueUnitsSegment?.map((item) => ({
      ...item,
      text: formatMessage({ id: item?.text || '' }),
    }));
  }, [locale, RevenueUnitsSegment]);

  const timePeriodOptions = [
    { text: formatMessage({ id: 'dbi_benchmark_m1_m12' }), value: '1m~12m' },
    { text: formatMessage({ id: 'dbi_benchmark_y1_y5' }), value: '1y~5y' },
  ];

  const [show, setShow] = useState(false);

  const timePeriodLabel = useMemo(() => {
    return timePeriodOptions?.find((item) => item.value === timePeriod)?.text;
  }, [timePeriod]);

  return (
    <CardSlotV2
      className="dbi-benchmark-cardbox dbi-benchmark-revenue-trends"
      iconList={[]}
    >
      <div>
        <Radio.Group
          value={timeBasis}
          disabled={loading}
          onChange={onTimeBasicChange}
        >
          <Radio name="original">
            {formatMessage({ id: 'dbi_benchmark_earliest_date_mobile' })}
          </Radio>
          <Radio name="aligned">
            {formatMessage({ id: 'dbi_benchmark_aligned_across_apps' })}
          </Radio>
          <Radio name="reordered">
            {formatMessage({ id: 'dbi_benchmark_reorder_base' })}
            <TipContext
              className="tip-icon"
              AWidth={555}
              placement="top"
              tipText={formatMessage({id: 'dbi_benchmark_reorder_base_info'})}
            />
          </Radio>
        </Radio.Group>
        <Select
          appearance="button"
          matchButtonWidth
          value={timePeriod}
          className="dbi-benchmark-time-period-select"
          button={
            <>
              <span className="time-period-select-label">
                {formatMessage({ id: 'dbi_benchmark_time_period' })}
              </span>
              <span>{timePeriodLabel || ''}</span>
            </>
          }
          onChange={onTimePeriodChange}
          options={timePeriodOptions}
        />
      </div>
      {loading ? (
        <LoadingTip className="dbi-benchmark-chart-loading" />
      ) : (
        <>
          <Summary
            segment={segment}
            timePeriod={timePeriod}
            summaryData={summaryData}
          />
          <div className="dbi-benchmark-revenue-trends-title">
            <div className="trends-title-container">
              <div className="trends-title">
                {formatMessage({ id: 'dbi_benchmark_trends_kpi_data'})}
              </div>
              <TipContext
                className="tip-icon"
                AWidth={555}
                placement="top"
                tipText={formatMessage({id: 'dbi_benchmark_trends_kpi_data_info'}, { type: formatMessage({id: `dbi_benchmark_${timePeriod}_info`}) })}
              />
            </div>
            <div className="trends-title-container">
              <div className="trends-title" style={{ paddingLeft: 30 }}>
                {formatMessage(
                  {
                    id: 'dbi_benchmark_regional_distribution',
                  },
                  {
                    type: formatMessage({
                      id: `dbi_benchmark_${activeSegment}_mobile`,
                    }),
                  },
                )}
              </div>
              <TipContext
                className="tip-icon"
                AWidth={555}
                placement="top"
                tipText={
                 <>
                  { formatMessage({id: 'dbi_benchmark_regional_distribution_revenue_info'}, {
                      type: formatMessage({
                      id: `dbi_benchmark_${activeSegment}_mobile_lower`,
                    }),
                  })}
                  <br />
                  {formatMessage({id: `dbi_benchmark_regional_distribution_${activeSegment}_calc`})}
                 </>
              }
              />
              {/* dbi_benchmark_regional_distribution_revenue_info */}
            </div>
          </div>
          <Segment
            options={segmentList}
            className="revenue-units-segment"
            value={activeSegment}
            onChange={(value) => {
              // setShow(true);
              setActiveSegment(value);
              setShow(false);
              // setTimeout(() => {
              //   setShow(false);
              // }, 150);
            }}
          />
          <Row>
            {loading || show ? (
              <LoadingTip className="dbi-benchmark-chart-loading" />
            ) : (
              <>
                <Col style={{ marginRight: 20 }}>
                  <div className="revenue-chart-title">
                    {formatMessage({
                      id: `dbi_benchmark_${activeSegment}_trend`,
                    })}
                    <TipContext
                      className="tip-icon"
                      AWidth={555}
                      placement="top"
                      tipText={
                        <>
                        {formatMessage({id: 'dbi_benchmark_bar_trend_info'}, {
                            type: formatMessage({
                              id: `dbi_benchmark_${activeSegment}_mobile_lower`,
                            }),
                          })
                        }
                        <br />
                        {formatMessage({id: `dbi_benchmark_${activeSegment}_mobile_calc`})}
                        </>
                      }
                    />
                  </div>
                  {(trendsData?.[activeSegment] || [])?.length > 0 ? (
                    <TrendsBarChart
                      segment={activeSegment}
                      timePeriod={timePeriod}
                      barData={trendsData?.[activeSegment] || []}
                    />
                  ) : (
                    <div className="revenue-nodata-container">
                      {formatMessage({ id: 'dbi_detail_no_data_under' })}
                    </div>
                  )}
                </Col>
                <Col style={{ marginLeft: 20 }}>
                  {(revenueRegional?.[activeSegment] || [])?.length > 0 ? (
                    <RegionalPieChart
                      segment={segment}
                      levelValue={levelValue}
                      trendsSegment={activeSegment}
                      pieData={revenueRegional?.[activeSegment] || []}
                    />
                  ) : (
                    <div className="revenue-nodata-container">
                      {formatMessage({ id: 'dbi_detail_no_data_under' })}
                    </div>
                  )}
                </Col>
              </>
            )}
          </Row>
        </>
      )}
    </CardSlotV2>
  );
};

RevenueTrends.displayName = 'RevenueTrends';

export default RevenueTrends;
